import React from 'react';
import { TouchableOpacity } from 'react-native';
import styled from '@emotion/native';
import { BodyText } from '@/shared/components/StyledText';
import { disabledDarkGrey, green, white } from '@/shared/styles/colors';
import { AvailabilityStep } from '@/shared/constants/availability';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { Feather } from '@expo/vector-icons';

const CIRCLE_SIZE = 22;
const LINE_THICKNESS = 1;
const MOBILE_LINE_OFFSET = 78;
const DESKTOP_LINE_OFFSET = 70;

interface StepperProps {
  currentStep: AvailabilityStep | null;
  completedSteps: AvailabilityStep[];
  onStepPress: (step: AvailabilityStep) => void;
}

const stepLabels = {
  [AvailabilityStep.CalendarConnection]: 'Connect',
  [AvailabilityStep.Availability]: 'Availability',
  [AvailabilityStep.Booking]: 'Booking',
  [AvailabilityStep.TimeZoneSelection]: 'Timezone',
  [AvailabilityStep.Schedule]: 'Schedule',
};

const Stepper = ({
  currentStep,
  completedSteps,
  onStepPress,
}: StepperProps) => {
  const { isDesktop } = useBreakpoint();
  const lineOffset = isDesktop ? DESKTOP_LINE_OFFSET : MOBILE_LINE_OFFSET;

  // Filter to get only numeric steps and sort them
  const steps = Object.values(AvailabilityStep)
    .filter((step) => typeof step === 'number')
    .sort((a, b) => Number(a) - Number(b)) as number[];

  return (
    <StepsContainer isDesktop={isDesktop}>
      {steps.map((step: AvailabilityStep, index) => {
        const isActive = currentStep === step;
        const isCompleted = completedSteps.includes(step);
        const isFirst = index === 0;
        const isLast = index === steps.length - 1;
        const nextStep = !isLast ? steps[index + 1] : null;
        const isNextStepActiveOrComplete =
          nextStep !== null &&
          (currentStep === nextStep || completedSteps.includes(nextStep));

        // Allow selecting if step is completed OR if it's the next step after a completed step
        const canNavigateToStep =
          isCompleted ||
          (index > 0 && completedSteps.includes(steps[index - 1]));

        return (
          <StepWrapper key={step}>
            {!isFirst && (
              <LeftLine
                isActive={isActive || isCompleted}
                lineOffset={lineOffset}
              />
            )}
            <RelativeCircleContainer>
              <TouchableWrapper
                onPress={() => onStepPress(step)}
                isDisabled={!canNavigateToStep}
              >
                <StepCircle isActive={isActive} isCompleted={isCompleted}>
                  {isCompleted && !isActive ? (
                    <Feather name="check" size={14} color={white} />
                  ) : (
                    <StepCircleText
                      isActive={isActive}
                      isCompleted={isCompleted}
                    >
                      {step + 1}
                    </StepCircleText>
                  )}
                </StepCircle>
                <StepLabel isActive={isActive} isCompleted={isCompleted}>
                  {stepLabels[step]}
                </StepLabel>
              </TouchableWrapper>
            </RelativeCircleContainer>
            {!isLast && (
              <RightLine
                isCompleted={isNextStepActiveOrComplete}
                lineOffset={lineOffset}
              />
            )}
          </StepWrapper>
        );
      })}
    </StepsContainer>
  );
};

export default Stepper;

const StepsContainer = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: isDesktop ? 530 : '100%',
  marginHorizontal: 'auto',
}));

const StepWrapper = styled.View({
  flex: 1,
  alignItems: 'center',
});

const TouchableWrapper = styled(TouchableOpacity)<{ isDisabled: boolean }>(
  ({ isDisabled }) => ({
    width: '100%',
    alignItems: 'center',
    pointerEvents: isDisabled ? 'none' : 'auto',
  }),
);

const RelativeCircleContainer = styled.View({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
});

const StepCircle = styled.View<{ isActive: boolean; isCompleted: boolean }>(
  ({ isActive, isCompleted }) => ({
    width: CIRCLE_SIZE,
    height: CIRCLE_SIZE,
    borderRadius: CIRCLE_SIZE / 2,
    borderWidth: isCompleted || isActive ? 2 : 1,
    borderColor: isCompleted || isActive ? green : disabledDarkGrey,
    backgroundColor: isCompleted && !isActive ? green : 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  }),
);

const StepCircleText = styled(BodyText)<{
  isActive: boolean;
  isCompleted: boolean;
}>(({ isActive, isCompleted }) => ({
  fontSize: 12,
  fontWeight: isActive || isCompleted ? 'bold' : 'normal',
  color: isCompleted || isActive ? green : disabledDarkGrey,
  textAlign: 'center',
  justifyContent: 'center',
}));

const LeftLine = styled.View<{ isActive: boolean; lineOffset: number }>(
  ({ isActive, lineOffset }) => ({
    position: 'absolute',
    left: 0,
    right: `${lineOffset}%`,
    top: CIRCLE_SIZE / 2 - LINE_THICKNESS / 2,
    height: LINE_THICKNESS,
    backgroundColor: isActive ? green : disabledDarkGrey,
  }),
);

const RightLine = styled.View<{ isCompleted: boolean; lineOffset: number }>(
  ({ isCompleted, lineOffset }) => ({
    position: 'absolute',
    left: `${lineOffset}%`,
    right: 0,
    top: CIRCLE_SIZE / 2 - LINE_THICKNESS / 2,
    height: LINE_THICKNESS,
    backgroundColor: isCompleted ? green : disabledDarkGrey,
  }),
);

const StepLabel = styled(BodyText)<{ isActive: boolean; isCompleted: boolean }>(
  ({ isActive, isCompleted }) => ({
    color: isCompleted || isActive ? green : disabledDarkGrey,
    fontWeight: isActive || isCompleted ? 'bold' : 'normal',
    fontSize: 10,
    textAlign: 'center',
    textDecorationLine: isCompleted && !isActive ? 'underline' : 'none',
    letterSpacing: 0,
  }),
);
